import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { COLORS, TYPOGRAPHY } from '../lib/styleGuide';
import { BottomNavigation } from '../components/BottomNavigation';
import { Show, Notification } from 'react-iconly';
import { SellerHeader } from '../components/SellerHeader';
import { uploadShopCoverPhoto, validateImageFile } from '../utils/imageUpload';
import { Image } from 'react-iconly';

// Zod schema for store settings validation
const storeSettingsSchema = z.object({
  storeName: z.string().min(1, 'Store name is required'),
  storeAddress: z.string().min(1, 'Store address is required'),
  storeApt: z.string().optional(),
  storeCity: z.string().min(1, 'City is required'),
  storeState: z.string().min(1, 'State is required'),
  storeZip: z.string().min(1, 'ZIP code is required'),
  storeCountry: z.string().optional(),
  acceptsPickup: z.boolean(),
  acceptsDelivery: z.boolean(),
  sellsNonPerishables: z.boolean(),
  sellsPerishables: z.boolean(),
  storeStatus: z.enum(['active', 'inactive', 'closed']),
  notes: z.string().optional(),
  storeHours: z.object({
    monday: z.object({
      open: z.string(),
      close: z.string(),
      isOpen: z.boolean()
    }),
    tuesday: z.object({
      open: z.string(),
      close: z.string(),
      isOpen: z.boolean()
    }),
    wednesday: z.object({
      open: z.string(),
      close: z.string(),
      isOpen: z.boolean()
    }),
    thursday: z.object({
      open: z.string(),
      close: z.string(),
      isOpen: z.boolean()
    }),
    friday: z.object({
      open: z.string(),
      close: z.string(),
      isOpen: z.boolean()
    }),
    saturday: z.object({
      open: z.string(),
      close: z.string(),
      isOpen: z.boolean()
    }),
    sunday: z.object({
      open: z.string(),
      close: z.string(),
      isOpen: z.boolean()
    })
  }).refine((hours) => {
    // Custom validation: at least one day must be open
    return Object.values(hours).some(day => day.isOpen);
  }, 'At least one day must be open'),
}).refine((data) => {
  // Custom validation: at least one fulfillment option must be selected
  return data.acceptsPickup || data.acceptsDelivery;
}, {
  message: 'Please select at least one fulfillment option',
  path: ['fulfillment']
}).refine((data) => {
  // Custom validation: at least one product type must be selected
  return data.sellsNonPerishables || data.sellsPerishables;
}, {
  message: 'Please select at least one product type',
  path: ['productTypes']
});

export function StoreSettings() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Onboarding status
  const { 
    loading: onboardingLoading, 
    error: onboardingError, 
    getStepStatus,
    fetchStatus
  } = useOnboardingStatus();
  
  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid, isDirty },
    reset,
  } = useForm({
    resolver: zodResolver(storeSettingsSchema),
    mode: 'onChange',
    defaultValues: {
      storeName: '',
      storeAddress: '',
      storeApt: '',
      storeCity: '',
      storeState: '',
      storeZip: '',
      storeCountry: '',
      acceptsPickup: true,
      acceptsDelivery: false,
      sellsNonPerishables: true,
      sellsPerishables: false,
      storeStatus: 'inactive',
      notes: '',
      storeHours: {
        monday: { open: '09:00', close: '21:00', isOpen: true },
        tuesday: { open: '09:00', close: '21:00', isOpen: true },
        wednesday: { open: '09:00', close: '21:00', isOpen: true },
        thursday: { open: '09:00', close: '21:00', isOpen: true },
        friday: { open: '09:00', close: '21:00', isOpen: true },
        saturday: { open: '10:00', close: '18:00', isOpen: true },
        sunday: { open: '10:00', close: '18:00', isOpen: true }
      }
    }
  });

  // Watch form values for real-time updates
  const watchedValues = watch();
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closing, setClosing] = useState(false);
  const [actualStoreDocId, setActualStoreDocId] = useState(null);
  const [coverPhotoUrl, setCoverPhotoUrl] = useState('');
  const [coverPhotoFile, setCoverPhotoFile] = useState(null);
  const coverPhotoInputRef = useRef(null);

  // Check if all required onboarding steps are completed
  const checkOnboardingRequirements = () => {
    const setupFeeStatus = getStepStatus('setup_fee');
    const identityStatus = getStepStatus('identity');
    const paymentStatus = getStepStatus('payment');
    const documentsStatus = getStepStatus('documents');
    const productStatus = getStepStatus('product');
    
    return setupFeeStatus === 'completed' && 
           identityStatus === 'completed' && 
           paymentStatus === 'completed' && 
           documentsStatus === 'completed' &&
           productStatus === 'completed';
  };

  // Load store settings
  useEffect(() => {
    const loadStoreSettings = async () => {
      if (!currentUser || !storeId) return;
      
      try {
        setLoading(true);
        setError('');
        
        let storeData = null;
        
        // Try to get store by ID first
        const storeRef = doc(db, 'shops', storeId);
        const storeDoc = await getDoc(storeRef);
        
        if (storeDoc.exists()) {
          storeData = storeDoc.data();
          setActualStoreDocId(storeId); // Use the URL storeId since document exists
          // Check if user owns this store
          if (storeData.sellerId !== currentUser.uid) {
            setError('You do not have permission to edit this store');
            return;
          }
        } else {
          // If no store by ID, query by sellerId (userId)
          console.log('Store not found by ID, querying by sellerId:', currentUser.uid);
          const shopsQuery = query(
            collection(db, 'shops'),
            where('sellerId', '==', currentUser.uid)
          );
          const shopsSnapshot = await getDocs(shopsQuery);
          
          if (!shopsSnapshot.empty) {
            const docData = shopsSnapshot.docs[0];
            storeData = docData.data();
            setActualStoreDocId(docData.id); // Track the actual document ID from query
            console.log('Found store by sellerId:', storeData, 'Doc ID:', docData.id);
          } else {
            setError('Store not found');
            return;
          }
        }
        
        // Load store settings
        console.log('StoreSettings: Loading store data:', storeData);
        console.log('StoreSettings: Field values - name:', storeData.name, 'shopName:', storeData.shopName, 'storeName:', storeData.storeName);
        console.log('StoreSettings: Address values:', storeData.address);
        
        setValue('storeName', storeData.name || storeData.shopName || storeData.storeName || '');
        setValue('storeAddress', storeData.address?.address || storeData.address?.street || storeData.street || storeData.storeAddress || '');
        setValue('storeApt', storeData.address?.apt || storeData.apt || '');
        setValue('storeCity', storeData.address?.city || storeData.city || storeData.storeCity || '');
        setValue('storeState', storeData.address?.state || storeData.state || storeData.storeState || '');
        setValue('storeZip', storeData.address?.zip || storeData.zip || storeData.storeZip || '');
        setValue('storeCountry', storeData.address?.country || storeData.country || storeData.storeCountry || '');
        setCoverPhotoUrl(storeData.coverPhotoURL || storeData.coverphotoURL || storeData.coverPhoto || storeData.coverPhotoUrl || storeData.coverImage || '');
        setValue('acceptsPickup', storeData.acceptsPickup !== undefined ? storeData.acceptsPickup : true);
        setValue('acceptsDelivery', storeData.acceptsDelivery || false);
        setValue('sellsNonPerishables', storeData.sellsNonPerishables !== undefined ? storeData.sellsNonPerishables : true);
        setValue('sellsPerishables', storeData.sellsPerishables || false);
        
        // Load store hours
        if (storeData.storeHours) {
          setValue('storeHours', storeData.storeHours);
        }
        
        // Check onboarding requirements and set store status accordingly
        const requirementsMet = checkOnboardingRequirements();
        const currentStatus = storeData.status || 'inactive';
        
        // Only allow active status if all requirements are met
        if (requirementsMet && currentStatus === 'active') {
          setValue('storeStatus', 'active');
        } else {
          setValue('storeStatus', 'inactive');
        }
        
        setValue('notes', storeData.description || storeData.notes || '');
        
        console.log('Store data populated:', {
          storeName: storeData.name || storeData.storeName,
          storeAddress: storeData.address?.address || storeData.address?.street,
          storeApt: storeData.address?.apt,
          notes: storeData.description || storeData.notes,
          fullStoreData: storeData,
          requirementsMet,
          currentStatus
        });
        
      } catch (err) {
        console.error('Error loading store settings:', err);
        setError('Failed to load store settings');
      } finally {
        setLoading(false);
      }
    };

    loadStoreSettings();
  }, [currentUser, storeId, getStepStatus, setValue]);

  // Monitor onboarding status changes and update store status accordingly
  useEffect(() => {
    if (!onboardingLoading && currentUser) {
      const requirementsMet = checkOnboardingRequirements();
      
      // If requirements are now met and store is currently inactive, allow activation
      if (requirementsMet && watchedValues.storeStatus === 'inactive') {
        // Don't auto-activate, just enable the toggle
        console.log('All onboarding requirements met - store can now be activated');
      } else if (!requirementsMet && watchedValues.storeStatus === 'active') {
        // If requirements are no longer met and store is active, force to inactive
        setValue('storeStatus', 'inactive');
        console.log('Onboarding requirements no longer met - store deactivated');
      }
    }
  }, [onboardingLoading, currentUser, getStepStatus, watchedValues.storeStatus, setValue]);

  // Refresh onboarding status when component mounts to ensure we have latest data
  useEffect(() => {
    if (currentUser && !onboardingLoading) {
      // Force a refresh of the onboarding status
      const refreshOnboardingStatus = async () => {
        try {
          // This will trigger the useOnboardingStatus hook to refresh
          // We can't directly call fetchStatus from here, but the hook should auto-refresh
          console.log('Refreshing onboarding status for store settings...');
          
          // Log current onboarding status for debugging
          const requirementsMet = checkOnboardingRequirements();
          console.log('Current onboarding requirements status:', {
            setupFee: getStepStatus('setup_fee'),
            identity: getStepStatus('identity'),
            payment: getStepStatus('payment'),
            documents: getStepStatus('documents'),
            product: getStepStatus('product'),
            requirementsMet
          });
          
          // Update store status based on current requirements
          if (requirementsMet && watchedValues.storeStatus === 'inactive') {
            console.log('All requirements met - store can now be activated');
          } else if (!requirementsMet && watchedValues.storeStatus === 'active') {
            console.log('Requirements no longer met - store should be deactivated');
            setValue('storeStatus', 'inactive');
          }
        } catch (error) {
          console.error('Error refreshing onboarding status:', error);
        }
      };
      
      refreshOnboardingStatus();
    }
  }, [currentUser, onboardingLoading, getStepStatus, watchedValues.storeStatus, setValue]);

  // Validation is now handled by React Hook Form + Zod

  // Handle cover photo upload
  const handleCoverPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        validateImageFile(file);
        setCoverPhotoFile(file);
        setCoverPhotoUrl(URL.createObjectURL(file));
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleCoverPhotoClick = () => {
    coverPhotoInputRef.current?.click();
  };

  // Save settings
  const handleSave = async (data) => {
    // Prevent saving if trying to set store to active without meeting requirements
    if (data.storeStatus === 'active' && !checkOnboardingRequirements()) {
      setError('Cannot activate store: Complete all required onboarding steps first');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Use the actual document ID we loaded
      const effectiveStoreId = actualStoreDocId || storeId;
      
      // Upload cover photo if a new file was selected
      let coverPhotoURL = coverPhotoUrl;
      if (coverPhotoFile && currentUser) {
        try {
          const uploadResult = await uploadShopCoverPhoto(coverPhotoFile, currentUser.uid);
          coverPhotoURL = uploadResult.url;
        } catch (uploadError) {
          console.error('Error uploading cover photo:', uploadError);
          setError('Failed to upload cover photo. Please try again.');
          setSaving(false);
          return;
        }
      }
      
      const storeRef = doc(db, 'shops', effectiveStoreId);
      const updateData = {
        name: data.storeName.trim(),
        address: {
          address: data.storeAddress.trim(),
          street: data.storeAddress.trim(),
          apt: data.storeApt.trim(),
          city: data.storeCity.trim(),
          state: data.storeState.trim(),
          zip: data.storeZip.trim(),
          country: (data.storeCountry && data.storeCountry.trim()) || 'US'
        },
        acceptsPickup: data.acceptsPickup,
        acceptsDelivery: data.acceptsDelivery,
        sellsNonPerishables: data.sellsNonPerishables,
        sellsPerishables: data.sellsPerishables,
        status: data.storeStatus,
        description: data.notes.trim(),
        storeHours: data.storeHours,
        updatedAt: new Date().toISOString()
      };
      
      // Only update cover photo if we have a URL (either existing or newly uploaded)
      if (coverPhotoURL) {
        updateData.coverPhotoURL = coverPhotoURL;
      }
      
      await updateDoc(storeRef, updateData);
      
      // Clear the file after successful upload
      if (coverPhotoFile) {
        setCoverPhotoFile(null);
      }
      
      setSuccess('Store settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Error saving store settings:', err);
      setError('Failed to save store settings');
    } finally {
      setSaving(false);
    }
  };

  // Close store permanently
  const handleCloseStore = async () => {
    try {
      setClosing(true);
      setError('');
      
      const effectiveStoreId = actualStoreDocId || storeId;
      const storeRef = doc(db, 'shops', effectiveStoreId);
      await updateDoc(storeRef, {
        status: 'closed',
        closedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      setShowCloseModal(false);
      setValue('storeStatus', 'closed');
      setSuccess('Store has been permanently closed');
      
      // Redirect to seller hub after 2 seconds
      setTimeout(() => navigate('/seller-hub'), 2000);
      
    } catch (err) {
      console.error('Error closing store:', err);
      setError('Failed to close store');
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <>
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `
        }} />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          background: COLORS.NEUTRAL_BG
        }}>
          <div style={{ 
            width: 32, 
            height: 32, 
            border: '3px solid transparent', 
            borderTop: '3px solid #C97B53', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }} />
        </div>
      </>
    );
  }

  if (error && !watchedValues.storeName) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: COLORS.NEUTRAL_BG,
        padding: '20px'
      }}>
        <h2 style={{ color: COLORS.ERROR_RED, marginBottom: '20px' }}>
          Error Loading Store
        </h2>
        <p style={{ color: COLORS.ACCENT_GRAY, marginBottom: '20px', textAlign: 'center' }}>
          {error}
        </p>
        <button
          onClick={() => navigate('/seller-hub')}
          style={{
            background: COLORS.PRIMARY_BROWN,
            color: COLORS.NEUTRAL_WHITE,
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          Return to Seller Hub
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: COLORS.NEUTRAL_BG, paddingBottom: '80px' }}>
      <SellerHeader />

      {/* Main Content */}
      <div style={{ paddingTop: '72px' }}>
        <div style={{ padding: '20px' }}>
          {/* Success/Error Messages */}
          {success && (
            <div style={{
              background: COLORS.SUCCESS_GREEN,
              color: COLORS.NEUTRAL_WHITE,
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {success}
            </div>
          )}
          
          {error && (
            <div style={{
              background: COLORS.ERROR_RED,
              color: COLORS.NEUTRAL_WHITE,
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Top Card - Store Information & Status */}
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            {/* Store Name and Status Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '24px',
              marginBottom: '24px'
            }}>
              {/* Store Name */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '8px',
                  fontWeight: 500
                }}>
                  Store name
                </label>
                <input
                  type="text"
                  {...register('storeName')}
                  readOnly
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: `1px solid #E5E7EB`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    background: '#F3F4F6',
                    color: COLORS.ACCENT_GRAY,
                    cursor: 'not-allowed'
                  }}
                  placeholder="Store name will be loaded automatically"
                />

              </div>
              

              
              {/* Store Status */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '8px',
                  fontWeight: 500
                }}>
                  Store status
                </label>
                {/* Progress indicator */}
                {!checkOnboardingRequirements() && (
                  <div style={{
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      flex: 1,
                      height: '6px',
                      background: '#E5E7EB',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        background: COLORS.PRIMARY_GRADIENT,
                        borderRadius: '3px',
                        width: `${(() => {
                          const steps = ['setup_fee', 'identity', 'payment', 'documents', 'product'];
                          const completedSteps = steps.filter(step => getStepStatus(step) === 'completed').length;
                          return Math.round((completedSteps / steps.length) * 100);
                        })()}%`,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <span style={{
                      fontSize: '10px',
                      color: COLORS.ACCENT_GRAY,
                      fontWeight: 500,
                      minWidth: '35px'
                    }}>
                      {(() => {
                        const steps = ['setup_fee', 'identity', 'payment', 'documents', 'product'];
                        const completedSteps = steps.filter(step => getStepStatus(step) === 'completed').length;
                        return `${completedSteps}/${steps.length}`;
                      })()}
                    </span>
                  </div>
                )}
                

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    position: 'relative',
                    width: '50px',
                    height: '24px'
                  }}>
                    <input
                      type="checkbox"
                      checked={watchedValues.storeStatus === 'active'}
                      onChange={(e) => {
                        console.log('Toggle changed to:', e.target.checked);
                        setValue('storeStatus', e.target.checked ? 'active' : 'inactive');
                      }}
                      style={{
                        opacity: 0,
                        width: 0,
                        height: 0
                      }}
                      onClick={() => {
                        console.log('Toggle clicked - current requirements status:', {
                          setupFee: getStepStatus('setup_fee'),
                          identity: getStepStatus('identity'),
                          payment: getStepStatus('payment'),
                          documents: getStepStatus('documents'),
                          product: getStepStatus('product'),
                          allMet: checkOnboardingRequirements()
                        });
                      }}
                    />
                    <span 
                      style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: watchedValues.storeStatus === 'active' ? COLORS.PRIMARY_GRADIENT : '#D1D5DB',
                        borderRadius: '24px',
                        transition: '0.3s',
                        opacity: 1
                      }}
                      onClick={() => {
                        console.log('Toggle span clicked!');
                        const newStatus = watchedValues.storeStatus === 'active' ? 'inactive' : 'active';
                        console.log('Changing status from', watchedValues.storeStatus, 'to', newStatus);
                        setValue('storeStatus', newStatus);
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        content: '""',
                        height: '18px',
                        width: '18px',
                        left: watchedValues.storeStatus === 'active' ? '3px' : '29px',
                        bottom: '3px',
                        background: COLORS.NEUTRAL_WHITE,
                        borderRadius: '50%',
                        transition: '0.3s'
                      }} />
                    </span>
                  </div>
                  <span style={{
                    fontSize: '14px',
                    color: COLORS.NEUTRAL_TEXT,
                    fontWeight: 500
                  }}>
                    {watchedValues.storeStatus === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {!checkOnboardingRequirements() ? (
                  <div style={{
                    fontSize: '12px',
                    color: COLORS.ERROR_RED,
                    margin: '8px 0 0 0',
                    lineHeight: 1.4,
                    padding: '8px 12px',
                    background: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '6px'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Requirements not met:</strong> Complete the following steps to activate your store:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: getStepStatus('setup_fee') === 'completed' ? COLORS.SUCCESS_GREEN || '#10B981' : COLORS.ERROR_RED }}>
                          {getStepStatus('setup_fee') === 'completed' ? '✅' : '❌'}
                        </span>
                        <span>Setup Fee Payment</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: getStepStatus('identity') === 'completed' ? COLORS.SUCCESS_GREEN || '#10B981' : COLORS.ERROR_RED }}>
                          {getStepStatus('identity') === 'completed' ? '✅' : '❌'}
                        </span>
                        <span>Identity Verification</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: getStepStatus('payment') === 'completed' ? COLORS.SUCCESS_GREEN || '#10B981' : COLORS.ERROR_RED }}>
                          {getStepStatus('payment') === 'completed' ? '✅' : '❌'}
                        </span>
                        <span>Bank Account Connection</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: getStepStatus('documents') === 'completed' ? COLORS.SUCCESS_GREEN || '#10B981' : COLORS.ERROR_RED }}>
                          {getStepStatus('documents') === 'completed' ? '✅' : '❌'}
                        </span>
                        <span>Required Documents</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: getStepStatus('product') === 'completed' ? COLORS.SUCCESS_GREEN || '#10B981' : COLORS.ERROR_RED }}>
                          {getStepStatus('product') === 'completed' ? '✅' : '❌'}
                        </span>
                        <span>First Product Created</span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        fetchStatus();
                        setError('');
                      }}
                      style={{
                        background: COLORS.PRIMARY_GRADIENT,
                        color: COLORS.NEUTRAL_WHITE,
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Refresh Status
                    </button>
                  </div>
                ) : (
                  <p style={{
                    fontSize: '12px',
                    color: COLORS.ACCENT_GRAY,
                    margin: '4px 0 0 0',
                    lineHeight: 1.4
                  }}>
                    Turn off to temporarily hide your shop from buyers.
                  </p>
                )}
              </div>
            </div>
            
            {/* Store Address */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: '8px',
                fontWeight: 500
              }}>
                Store address
              </label>
              
              {/* Street Address */}
              <input
                type="text"
                {...register('storeAddress')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: errors.storeAddress ? `2px solid ${COLORS.ERROR_RED}` : `1px solid #E5E7EB`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: '#F9FAFB',
                  marginBottom: '12px'
                }}
                placeholder="Street address"
              />
              
              {/* Apartment/Unit */}
              <input
                type="text"
                {...register('storeApt')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `1px solid #E5E7EB`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: '#F9FAFB',
                  marginBottom: '12px'
                }}
                placeholder="Apartment, suite, unit, etc. (optional)"
              />
              
              {/* City and State Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <input
                  type="text"
                  {...register('storeCity')}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.storeCity ? `2px solid ${COLORS.ERROR_RED}` : `1px solid #E5E7EB`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    background: '#F9FAFB'
                  }}
                  placeholder="City"
                />
                <input
                  type="text"
                  {...register('storeState')}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.storeState ? `2px solid ${COLORS.ERROR_RED}` : `1px solid #E5E7EB`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    background: '#F9FAFB'
                  }}
                  placeholder="State"
                />
              </div>
              
              {/* ZIP */}
              <div>
                <input
                  type="text"
                  {...register('storeZip')}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.storeZip ? `2px solid ${COLORS.ERROR_RED}` : `1px solid #E5E7EB`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    background: '#F9FAFB'
                  }}
                  placeholder="ZIP"
                />
              </div>
              
              {/* Address Validation Errors */}
              {(errors.storeAddress || errors.storeCity || errors.storeState || errors.storeZip) && (
                <div style={{ marginTop: '8px' }}>
                  {errors.storeAddress && (
                    <p style={{ color: COLORS.ERROR_RED, fontSize: '12px', margin: '0 0 4px 0' }}>
                      {errors.storeAddress.message}
                    </p>
                  )}
                  {errors.storeCity && (
                    <p style={{ color: COLORS.ERROR_RED, fontSize: '12px', margin: '0 0 4px 0' }}>
                      {errors.storeCity.message}
                    </p>
                  )}
                  {errors.storeState && (
                    <p style={{ color: COLORS.ERROR_RED, fontSize: '12px', margin: '0 0 4px 0' }}>
                      {errors.storeState.message}
                    </p>
                  )}
                  {errors.storeZip && (
                    <p style={{ color: COLORS.ERROR_RED, fontSize: '12px', margin: '0 0 4px 0' }}>
                      {errors.storeZip.message}
                    </p>
                  )}
                </div>
              )}
              
              <p style={{
                fontSize: '12px',
                color: COLORS.ACCENT_GRAY,
                margin: '8px 0 0 0',
                lineHeight: 1.4
              }}>
                Your address is only shown to customers after purchase for pick-up orders.
              </p>
            </div>
          </div>

          {/* Cover Photo & Store Description */}
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {/* Cover Photo */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '12px',
                  fontWeight: 500
                }}>
                  Cover Photo
                </label>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  {coverPhotoUrl ? (
                    <div style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
                      <img
                        src={coverPhotoUrl}
                        alt="Store cover"
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: '2px solid #E5E7EB'
                        }}
                        onClick={handleCoverPhotoClick}
                      />
                      <button
                        type="button"
                        onClick={handleCoverPhotoClick}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          padding: '8px 12px',
                          background: 'rgba(0, 0, 0, 0.6)',
                          color: COLORS.NEUTRAL_WHITE,
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleCoverPhotoClick}
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: '200px',
                        border: '2px dashed #ddd',
                        borderRadius: '8px',
                        background: '#FAF9F7',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'border 0.2s'
                      }}
                      aria-label="Upload cover photo"
                    >
                      <Image 
                        set="regular" 
                        size={32} 
                        primaryColor={COLORS.ACCENT_GRAY}
                      />
                      <span style={{ fontSize: '14px', color: COLORS.ACCENT_GRAY, textAlign: 'center', fontWeight: 500 }}>
                        Click to upload cover photo
                      </span>
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        JPG, PNG, GIF, WebP up to 5MB
                      </span>
                    </button>
                  )}
                  <input
                    ref={coverPhotoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCoverPhotoChange}
                    style={{ display: 'none' }}
                    aria-label="Cover photo file input"
                  />
                </div>
                <p style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  margin: '0',
                  lineHeight: 1.4
                }}>
                  Click on the image to change or upload a new cover photo for your store.
                </p>
              </div>

              {/* Store Description */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '8px',
                  fontWeight: 500
                }}>
                  Store description
                </label>
                <textarea
                  {...register('notes')}
                  style={{
                    width: '100%',
                    minHeight: '200px',
                    padding: '12px 16px',
                    border: `1px solid #E5E7EB`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    background: '#F9FAFB',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Hours, parking, pick-up instructions..."
                />
                <p style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  margin: '8px 0 0 0',
                  lineHeight: 1.4
                }}>
                  Add details about your store that customers should know.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Card - Fulfillment & Product Types */}
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            {/* Fulfillment and Product Types Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '24px',
              marginBottom: '24px'
            }}>
              {/* Fulfillment */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '12px',
                  fontWeight: 500
                }}>
                  Fulfillment
                </label>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: COLORS.NEUTRAL_TEXT,
                    position: 'relative'
                  }}>
                    <input
                      type="checkbox"
                      checked={watchedValues.acceptsPickup}
                      onChange={(e) => setValue('acceptsPickup', e.target.checked)}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: `2px solid ${watchedValues.acceptsPickup ? COLORS.PRIMARY_BROWN_DARK : '#D1D5DB'}`,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: watchedValues.acceptsPickup ? COLORS.PRIMARY_GRADIENT : 'white',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}>
                      {watchedValues.acceptsPickup && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    Pick-up
                  </label>
                  
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: COLORS.NEUTRAL_TEXT,
                    position: 'relative'
                  }}>
                    <input
                      type="checkbox"
                      checked={watchedValues.acceptsDelivery}
                      onChange={(e) => setValue('acceptsDelivery', e.target.checked)}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: `2px solid ${watchedValues.acceptsDelivery ? COLORS.PRIMARY_BROWN_DARK : '#D1D5DB'}`,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: watchedValues.acceptsDelivery ? COLORS.PRIMARY_GRADIENT : 'white',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}>
                      {watchedValues.acceptsDelivery && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        )}
                    </div>
                    Delivery
                  </label>
                </div>
                
                {errors.fulfillment && (
                  <p style={{
                    color: COLORS.ERROR_RED,
                    fontSize: '12px',
                    margin: '8px 0 0 0'
                  }}>
                    {errors.fulfillment.message}
                  </p>
                )}
                
                <p style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  margin: '8px 0 0 0',
                  lineHeight: 1.4
                }}>
                  Choose how customers receive orders.
                </p>
              </div>
              
              {/* Product Types */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: '12px',
                  fontWeight: 500
                }}>
                  What do you sell?
                </label>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: COLORS.NEUTRAL_TEXT,
                    position: 'relative'
                  }}>
                    <input
                      type="checkbox"
                      checked={watchedValues.sellsNonPerishables}
                      onChange={(e) => setValue('sellsNonPerishables', e.target.checked)}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: `2px solid ${watchedValues.sellsNonPerishables ? COLORS.PRIMARY_BROWN_DARK : '#D1D5DB'}`,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: watchedValues.sellsNonPerishables ? COLORS.PRIMARY_GRADIENT : 'white',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}>
                      {watchedValues.sellsNonPerishables && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    Non-perishables
                  </label>
                  
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: COLORS.NEUTRAL_TEXT,
                    position: 'relative'
                  }}>
                    <input
                      type="checkbox"
                      checked={watchedValues.sellsPerishables}
                      onChange={(e) => setValue('sellsPerishables', e.target.checked)}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: `2px solid ${watchedValues.sellsPerishables ? COLORS.PRIMARY_BROWN_DARK : '#D1D5DB'}`,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: watchedValues.sellsPerishables ? COLORS.PRIMARY_GRADIENT : 'white',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}>
                      {watchedValues.sellsPerishables && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    Perishables
                  </label>
                </div>
                
                {errors.productTypes && (
                  <p style={{
                    color: COLORS.ERROR_RED,
                    fontSize: '12px',
                    margin: '8px 0 0 0'
                  }}>
                    {errors.productTypes.message}
                  </p>
                )}
                
                <p style={{
                  fontSize: '12px',
                  color: COLORS.ACCENT_GRAY,
                  margin: '8px 0 0 0',
                  lineHeight: 1.4
                }}>
                  Perishables may require additional licenses depending on your location.
                </p>
              </div>
            </div>
          </div>

          {/* Store Hours Section */}
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <label style={{
              display: 'block',
              fontSize: '16px',
              color: COLORS.NEUTRAL_TEXT,
              marginBottom: '16px',
              fontWeight: 600
            }}>
              Store Hours
            </label>
            
            <p style={{
              fontSize: '14px',
              color: COLORS.ACCENT_GRAY,
              marginBottom: '20px',
              lineHeight: 1.4
            }}>
              Set your default store hours. These will apply to all products unless overridden at the product level.
            </p>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {Object.entries(watchedValues.storeHours)
                .sort(([a], [b]) => {
                  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                  return days.indexOf(a.toLowerCase()) - days.indexOf(b.toLowerCase());
                })
                .map(([day, hours]) => (
                <div key={day} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  padding: '12px',
                  background: '#F9FAFB',
                  borderRadius: '8px',
                  border: `1px solid ${hours.isOpen ? '#E5E7EB' : '#F3F4F6'}`
                }}>
                  {/* Day Label and Toggle Row */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: COLORS.NEUTRAL_TEXT,
                      textTransform: 'capitalize'
                    }}>
                      {day}
                    </div>
                    
                    {/* Open/Closed Toggle */}
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={hours.isOpen}
                        onChange={(e) => setValue(`storeHours.${day}.isOpen`, e.target.checked)}
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          cursor: 'pointer'
                        }}
                      />
                      <div style={{
                        width: '40px',
                        height: '24px',
                        background: hours.isOpen ? COLORS.PRIMARY_GRADIENT : '#D1D5DB',
                        borderRadius: '12px',
                        position: 'relative',
                        transition: 'all 0.2s ease'
                      }}>
                        <span style={{
                          position: 'absolute',
                          top: '2px',
                          left: hours.isOpen ? '18px' : '2px',
                          width: '20px',
                          height: '20px',
                          background: COLORS.NEUTRAL_WHITE,
                          borderRadius: '50%',
                          transition: 'all 0.2s ease'
                        }} />
                      </div>
                    </label>
                  </div>
                  
                  {/* Time Inputs Row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px'
                  }}>
                    {/* Open Time */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        color: COLORS.ACCENT_GRAY,
                        marginBottom: '4px'
                      }}>
                        Open
                      </label>
                      <input
                        type="time"
                        {...register(`storeHours.${day}.open`)}
                        disabled={!hours.isOpen}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: `1px solid ${hours.isOpen ? '#E5E7EB' : '#F3F4F6'}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          outline: 'none',
                          background: hours.isOpen ? COLORS.NEUTRAL_WHITE : '#F3F4F6',
                          color: hours.isOpen ? COLORS.NEUTRAL_TEXT : COLORS.ACCENT_GRAY,
                          cursor: hours.isOpen ? 'text' : 'not-allowed'
                        }}
                      />
                    </div>
                    
                    {/* Close Time */}
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '12px',
                        color: COLORS.ACCENT_GRAY,
                        marginBottom: '4px'
                      }}>
                        Close
                      </label>
                      <input
                        type="time"
                        {...register(`storeHours.${day}.close`)}
                        disabled={!hours.isOpen}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: `1px solid ${hours.isOpen ? '#E5E7EB' : '#F3F4F6'}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          outline: 'none',
                          background: hours.isOpen ? COLORS.NEUTRAL_WHITE : '#F3F4F6',
                          color: hours.isOpen ? COLORS.NEUTRAL_TEXT : COLORS.ACCENT_GRAY,
                          cursor: hours.isOpen ? 'text' : 'not-allowed'
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <p style={{
              fontSize: '12px',
              color: COLORS.ACCENT_GRAY,
              margin: '16px 0 0 0',
              lineHeight: 1.4
            }}>
              Products can override these hours for specific days. Leave closed for days you don't operate.
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                padding: '8px 16px',
                background: COLORS.NEUTRAL_WHITE,
                color: COLORS.NEUTRAL_TEXT,
                border: `1px solid #E5E7EB`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Cancel
            </button>
            
            <button
              onClick={async (e) => {
                e.preventDefault();
                await handleSave(watchedValues);
              }}
              disabled={saving}
              style={{
                padding: '8px 16px',
                background: COLORS.PRIMARY_GRADIENT,
                color: COLORS.NEUTRAL_WHITE,
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>

          {/* Close Shop Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <button
              onClick={() => setShowCloseModal(true)}
              disabled={watchedValues.storeStatus === 'closed'}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                color: watchedValues.storeStatus === 'closed' ? COLORS.ACCENT_GRAY : COLORS.ERROR_RED,
                border: 'none',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: watchedValues.storeStatus === 'closed' ? 'not-allowed' : 'pointer',
                opacity: watchedValues.storeStatus === 'closed' ? 0.7 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              {watchedValues.storeStatus === 'closed' ? 'Store Closed' : 'Close Shop Permanently'}
            </button>
          </div>
        </div>
      </div>

      {/* Close Shop Confirmation Modal */}
      {showCloseModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: COLORS.NEUTRAL_WHITE,
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{
              fontSize: '20px',
              color: COLORS.ERROR_RED,
              margin: '0 0 16px 0',
              fontWeight: 600
            }}>
              Close Shop Permanently?
            </h3>
            
            <p style={{
              fontSize: '16px',
              color: COLORS.NEUTRAL_TEXT,
              margin: '0 0 24px 0',
              lineHeight: 1.5
            }}>
              Are you sure you want to permanently close your shop? This action cannot be undone and will remove your store from the marketplace.
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowCloseModal(false)}
                disabled={closing}
                style={{
                  flex: 1,
                  background: COLORS.NEUTRAL_WHITE,
                  color: COLORS.PRIMARY_BROWN,
                  border: `2px solid ${COLORS.PRIMARY_BROWN}`,
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: closing ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={handleSubmit(handleCloseStore)}
                disabled={closing}
                style={{
                  flex: 1,
                  background: COLORS.ERROR_RED,
                  color: COLORS.NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: closing ? 'not-allowed' : 'pointer'
                }}
              >
                {closing ? 'Closing...' : 'Close Shop'}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
}
